import { i18n } from "@tellescope/i18n";
import { Button, Calendar, cn, Label, Popover, PopoverContent, PopoverTrigger, TimeInput } from "@tellescope/ui";
import { formatDate, formatDateTime, formatTime, isValidDate } from "@tellescope/utils";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface EditableDateCellProps {
  value?: Date | number | string;
  onChange: (newValue: Date | null) => void | Promise<void>;
  disabled?: boolean;
  hideTime?: boolean;
  showSeconds?: boolean;
  emptyLabel?: string;
  className?: string;
  relative?: Parameters<typeof formatDateTime>[2];
  getBadgeColors?: (value: Date | number | string) =>
    | {
        bgColor: string;
        textColor: string;
      }
    | undefined;
  variant?: "badge" | "text";
}

export function EditableDateCell({
  value,
  onChange,
  disabled = false,
  hideTime = false,
  showSeconds = false,
  emptyLabel = i18n.t("Not set", { ns: "tickets" }),
  className,
  relative,
  getBadgeColors,
  variant = "badge",
}: EditableDateCellProps) {
  const { t } = useTranslation("tickets");
  const [open, setOpen] = useState(false);

  // Convert value to Date object
  const dateValue = useMemo(() => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return isValidDate(date) ? date : null;
  }, [value]);

  // Format display value
  const displayValue = useMemo(() => {
    if (!dateValue) return emptyLabel;

    if (hideTime) {
      return formatDate(dateValue, "MMM D, YYYY", relative);
    } else if (showSeconds) {
      return formatDateTime(dateValue, "MMM D, YYYY at hh:mm:ss a", relative);
    }
    return formatDateTime(dateValue, "MMM D, YYYY at hh:mm a", relative);
  }, [dateValue, emptyLabel, hideTime, showSeconds, relative]);

  // Time value for time input
  const timeValue = useMemo(
    () => (dateValue && !hideTime ? formatTime(dateValue, showSeconds ? "HH:mm:ss" : "HH:mm") : ""),
    [dateValue, hideTime, showSeconds]
  );

  const badgeColors = useMemo(() => {
    const defaultColors = {
      bgColor: "bg-muted hover:bg-gray-300",
      textColor: "text-muted-foreground hover:text-muted-foreground",
    };

    if (value === undefined) return defaultColors;

    return getBadgeColors?.(value) ?? defaultColors;
  }, [getBadgeColors, value]);

  const handleDateSelect = useCallback(
    (selectedDate: Date | undefined) => {
      if (!selectedDate) {
        onChange(null);
        if (hideTime) {
          setOpen(false);
        }
        return;
      }

      if (!hideTime && timeValue) {
        // Preserve time when selecting date
        const timeParts = timeValue.split(":").map(Number);
        const hours = timeParts[0] ?? 0;
        const minutes = timeParts[1] ?? 0;
        const seconds = timeParts[2] ?? 0;
        selectedDate.setHours(hours, minutes, showSeconds ? seconds : 0, 0);
      } else if (!timeValue) {
        // Default to current time if no previous time
        const currentTime = new Date();
        selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes(), 0, 0);
      }

      if (hideTime) {
        setOpen(false);
      }

      onChange(selectedDate);
    },
    [onChange, hideTime, timeValue, showSeconds]
  );

  const handleTimeChange = useCallback(
    (newTime: string) => {
      if (newTime) {
        const newTimeParts = newTime.split(":").map(Number);
        const hours = newTimeParts[0] ?? 0;
        const minutes = newTimeParts[1] ?? 0;
        const seconds = newTimeParts[2] ?? 0;
        const newDate = dateValue ? new Date(dateValue) : new Date();
        newDate.setHours(hours, minutes, showSeconds ? seconds : 0, 0);
        onChange(newDate);
      }
    },
    [dateValue, onChange, showSeconds]
  );

  if (disabled) {
    return <div className={cn("text-sm text-muted-foreground", className)}>{displayValue}</div>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={variant === "text" ? "link" : "ghost"}
          className={cn(
            "h-auto justify-start font-medium px-2 py-1 text-xs",
            variant === "badge" && badgeColors.bgColor,
            variant === "badge" && badgeColors.textColor,
            variant === "text" && "underline px-1 font-normal",
            !dateValue && "text-muted-foreground",
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 flex flex-col" sideOffset={10}>
        <Calendar mode="single" selected={dateValue || undefined} onSelect={handleDateSelect} />
        {!hideTime && (
          <div className="border-t p-3">
            <Label className="mb-2 block text-sm font-medium">{t("Time")}</Label>
            <TimeInput
              value={timeValue}
              onChange={handleTimeChange}
              showSeconds={showSeconds}
              stepSeconds={showSeconds ? 1 : 15}
              className="w-full"
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
